import { useState } from "react";
import { Link, useLocation } from "wouter";
import { AuthLayout } from "../components/AuthLayout";

/**
 * /merchant/signup — Phase 28 step 1 of 3 (collect form, mint OTP).
 *
 * On success the server returns an `attemptToken` we stash in
 * sessionStorage (NOT localStorage — this is in-flight signup state,
 * not a long-lived session). The verify page reads it back, the
 * cancel/complete steps clear it.
 *
 * D95: when the server flags PHONE_TAKEN / EMAIL_TAKEN / PREFIX_TAKEN
 * we surface the precise message PLUS a "sign in instead" link to
 * /merchant/login. That tradeoff is documented in DECISIONS.md — the
 * tiny enumeration-attack surface is worth the much better UX.
 */
const ATTEMPT_KEY = "orbit-merchant-signup-attempt";

interface AttemptStash {
  attemptToken: string;
  ownerPhoneMasked: string;
  expiresAt: string;
}

export function setSignupAttempt(s: AttemptStash): void {
  sessionStorage.setItem(ATTEMPT_KEY, JSON.stringify(s));
}

export function getSignupAttempt(): AttemptStash | null {
  const raw = sessionStorage.getItem(ATTEMPT_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AttemptStash;
  } catch {
    return null;
  }
}

export function clearSignupAttempt(): void {
  sessionStorage.removeItem(ATTEMPT_KEY);
}

export function MerchantSignupPage() {
  const [, navigate] = useLocation();
  const [businessName, setBusinessName] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [ownerPhone, setOwnerPhone] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [voucherPrefix, setVoucherPrefix] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // When `signInHint` is true we render a "Sign in instead" link
  // alongside the error — only set on PHONE/EMAIL/PREFIX_TAKEN.
  const [signInHint, setSignInHint] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setSignInHint(false);
    if (!businessName.trim() || !ownerName.trim() || !ownerPhone.trim() || !ownerEmail.trim() || !voucherPrefix.trim()) {
      setErr("Please fill in every field.");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch("/api/v1/public/merchant-signup/initiate", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          businessName: businessName.trim(),
          ownerName: ownerName.trim(),
          ownerPhone: ownerPhone.trim(),
          ownerEmail: ownerEmail.trim().toLowerCase(),
          voucherPrefix: voucherPrefix.trim().toUpperCase(),
        }),
      });
      const json = (await res.json().catch(() => null)) as
        | {
            data?: AttemptStash;
            error?: { code?: string; message?: string };
          }
        | null;
      if (!res.ok || !json?.data) {
        const code = json?.error?.code ?? "ERROR";
        if (code === "PHONE_TAKEN" || code === "EMAIL_TAKEN" || code === "PREFIX_TAKEN") {
          setSignInHint(true);
        }
        setErr(json?.error?.message ?? "Could not start signup. Please try again.");
        return;
      }
      setSignupAttempt(json.data);
      navigate("/merchant/signup/verify");
    } catch {
      setErr("Network error. Please try again in a moment.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title="Open your Orbit account"
      subtitle="We'll text a 6-digit code to confirm your phone."
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label htmlFor="signup-business" className="label">
            Business name
          </label>
          <input
            id="signup-business"
            type="text"
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            placeholder="Bert's Coffee Shop"
            className="input"
            data-testid="input-signup-business-name"
            maxLength={100}
          />
        </div>
        <div>
          <label htmlFor="signup-owner-name" className="label">
            Your name
          </label>
          <input
            id="signup-owner-name"
            type="text"
            value={ownerName}
            onChange={(e) => setOwnerName(e.target.value)}
            placeholder="Roberto Cruz"
            className="input"
            data-testid="input-signup-owner-name"
            maxLength={80}
          />
        </div>
        <div>
          <label htmlFor="signup-phone" className="label">
            Mobile number
          </label>
          <input
            id="signup-phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={ownerPhone}
            onChange={(e) => setOwnerPhone(e.target.value)}
            placeholder="+639171234567"
            className="input font-mono"
            data-testid="input-signup-owner-phone"
          />
          <p className="mt-1 text-xs text-ink-dark">
            Philippine mobile only — start with +63 or 09.
          </p>
        </div>
        <div>
          <label htmlFor="signup-email" className="label">
            Owner email
          </label>
          <input
            id="signup-email"
            type="email"
            autoComplete="email"
            value={ownerEmail}
            onChange={(e) => setOwnerEmail(e.target.value)}
            placeholder="owner@yourbusiness.ph"
            className="input"
            data-testid="input-signup-owner-email"
          />
          <p className="mt-1 text-xs text-ink-dark">
            Used for token recovery. We never spam.
          </p>
        </div>
        <div>
          <label htmlFor="signup-prefix" className="label">
            Voucher prefix
          </label>
          <input
            id="signup-prefix"
            type="text"
            value={voucherPrefix}
            onChange={(e) => setVoucherPrefix(e.target.value.toUpperCase())}
            placeholder="BERTS"
            className="input font-mono uppercase"
            data-testid="input-signup-voucher-prefix"
            maxLength={6}
          />
          <p className="mt-1 text-xs text-ink-dark">
            2–6 uppercase letters or digits. Appears on every voucher
            you issue (e.g. <span className="font-mono">BERTS-AB12CD</span>).
          </p>
        </div>
        {err && (
          <div className="rounded-card bg-red-50 px-3 py-2 text-sm text-red-700">
            <p data-testid="text-signup-error">{err}</p>
            {signInHint && (
              <Link
                href="/merchant/login"
                className="block mt-2 text-navy underline"
                data-testid="link-signup-sign-in-instead"
              >
                Sign in instead →
              </Link>
            )}
          </div>
        )}
        <button
          type="submit"
          disabled={busy}
          className="btn-primary w-full"
          data-testid="button-signup-initiate"
        >
          {busy ? "Sending code…" : "Send verification code"}
        </button>
        <Link
          href="/for-merchants"
          className="block text-center text-sm text-navy underline"
          data-testid="link-signup-cancel"
        >
          Cancel
        </Link>
      </form>
    </AuthLayout>
  );
}
